import { useCallback, useState } from 'react';
import { Button, DialogContent } from '@mui/material';
import { queueModal } from '@components/Modals/modalSlice';
import { store } from '@reduxStore';
import ApplicationModal from './ApplicationModal';

import styles from './ApplicationModal.module.css';

export interface ConfirmPopupProps {
  /** Callback method executed when/if confirmed */
  confirmedAction: () => void;
  /** Callback method executed when the pop up closes independent of whether or not the user confirmed */
  onCloseAction?: () => void;
  /** Callback method executed once the pop up has finished closing and been removed from the modal queue */
  onExitedAction?: () => void;
  /** Message to display explaining why the pop up is displaying */
  prompt: string;
}

/** Async function to add a confirmation prompt. Resolves once the prompt is fully closed, so another modal can safely follow it. */
export function confirmAsync(prompt: string) {
  return new Promise<boolean>((resolve) => {
    let confirmed = false;
    store.dispatch(
      queueModal({
        Component: 'confirmPopup',
        Props: {
          prompt,
          confirmedAction: () => {
            confirmed = true;
          },
          // Closing pops the top-most modal, so a follow-up modal queued before then would be removed instead of this one
          onExitedAction: () => {
            resolve(confirmed);
          }
        }
      })
    );
  });
}

/**
 * Confirmation Modal Component with Yes/No options and customizable prompt
 */
export default function ConfirmPopup(props: ConfirmPopupProps) {
  const [isOpen, setIsOpen] = useState(true);

  const onModalClose = useCallback(() => {
    if (props.onCloseAction) props.onCloseAction();
    setIsOpen(false);
  }, [props]);

  const onConfirm = () => {
    props.confirmedAction();
    onModalClose();
  };

  return (
    <ApplicationModal
      title='Are You Sure?'
      isOpen={isOpen}
      onClose={onModalClose}
      onExited={props.onExitedAction}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.repeat && !(event.target instanceof HTMLButtonElement)) {
          event.preventDefault();
          onConfirm();
        }
      }}
      closeButtonText='No'
      actions={
        <>
          <Button
            onClick={onConfirm}
            variant='contained'>
            Yes
          </Button>
        </>
      }>
      <DialogContent>
        <span className={styles.prompt}>{props.prompt}</span>
      </DialogContent>
    </ApplicationModal>
  );
}
